import { Injectable, InjectionToken, inject } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Observable, catchError, map, of } from 'rxjs';
import { SKIP_ERROR_NOTIFICATION } from '@core/http/http-error.interceptor';
import { AUTH_TOKEN_TYPE } from '@core/auth/auth-token.context';

/**
 * `GET https://shiftat.sa/api/pricing`. Held in a token rather than read from `environment`
 * so it can be varied in tests, matching `CONVERSIONS_API_URL`. An empty value — the default
 * wherever it isn't provided — skips the request, leaving callers on their built-in prices.
 */
export const PRICING_API_URL = new InjectionToken<string>('PRICING_API_URL', {
  providedIn: 'root',
  factory: () => '',
});

export type PricingTier = 'standard' | 'professional' | 'premium';

/** 30-day, VAT-inclusive SAR price per tier. A tier is absent when the endpoint omitted it
 *  or sent something that isn't a usable price. */
export type TierPrices = Partial<Record<PricingTier, number>>;

interface PricingResponse {
  data?: Record<string, unknown>;
}

const TIERS: readonly PricingTier[] = ['standard', 'professional', 'premium'];

@Injectable({ providedIn: 'root' })
export class PricingApiService {
  private readonly http = inject(HttpClient);
  private readonly url = inject(PRICING_API_URL);

  /**
   * Never errors: on any failure it emits `{}` so the pricing table falls back to its
   * built-in prices rather than leaving the employer without a way to pay.
   */
  getPrices(): Observable<TierPrices> {
    if (!this.url) return of({});

    // 'none' keeps the company JWT off this third-party origin — AUTH_TOKEN_TYPE defaults
    // to 'company', so leaving it unset would attach it.
    const context = new HttpContext().set(AUTH_TOKEN_TYPE, 'none').set(SKIP_ERROR_NOTIFICATION, true);

    return this.http.get<PricingResponse>(this.url, { context }).pipe(
      map((res) => {
        const prices: TierPrices = {};
        for (const tier of TIERS) {
          const value = res?.data?.[tier];
          if (typeof value === 'number' && Number.isFinite(value) && value >= 0) prices[tier] = value;
        }
        return prices;
      }),
      catchError(() => of({})),
    );
  }
}
